/**
 * BIM Model Viewer Component
 * Supports multiple Autodesk viewer URLs or local IFC files
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useApp } from '../../../store/AppContext';
import { api } from '../../../services/api';
import '../../../components/viewers/ViewerCommon.css';

function useIsMobile(breakpoint = 700) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [breakpoint]);
  return isMobile;
}

export function ModelViewer() {
  const { state, updateProject, setStatus } = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isMobile);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<any>(null);

  // Auto-collapse sidebar when switching to mobile
  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [isMobile]);

  const project = state.currentProject;

  // Backward compatibility: migrate legacy 'model' field to 'models' array
  const legacyModel = project?.model;
  const models = project?.models || [];

  // Migrate legacy model to models array if needed
  useEffect(() => {
    if (project && legacyModel && models.length === 0) {
      updateProject(project.id, {
        models: [legacyModel],
        model: undefined,
      });
    }
  }, [project, legacyModel, models.length, updateProject]);

  // Get the currently selected model
  const selectedModel = models.find(m => m.id === selectedModelId) || null;

  // Auto-select first model if none selected
  useEffect(() => {
    if (models.length > 0 && !selectedModelId) {
      setSelectedModelId(models[0].id);
    } else if (models.length === 0) {
      setSelectedModelId(null);
    }
  }, [models, selectedModelId]);

  // Resolve short/redirect URLs server-side to avoid X-Frame-Options on intermediate redirects
  const resolveEmbedUrl = useCallback(async (url: string): Promise<string> => {
    try {
      const resp = await fetch(`/api/resolve-embed-url?url=${encodeURIComponent(url)}`);
      if (resp.ok) {
        const data = await resp.json();
        return data.resolvedUrl || url;
      }
    } catch (err) {
      console.error('[ModelViewer] Failed to resolve embed URL:', err);
    }
    return url;
  }, []);

  // Create and manage iframe for external viewers
  useEffect(() => {
    if (!selectedModel?.externalUrl || selectedModel.type !== 'external') {
      if (iframeRef.current && containerRef.current) {
        try {
          if (containerRef.current.contains(iframeRef.current)) {
            containerRef.current.removeChild(iframeRef.current);
          }
        } catch (e) {
          // Ignore cleanup errors
        }
        iframeRef.current = null;
      }
      return;
    }

    if (!containerRef.current) return;

    setIsLoading(true);
    setError(null);

    let cancelled = false;

    // Resolve the URL server-side to skip redirect chains that set X-Frame-Options
    resolveEmbedUrl(selectedModel.externalUrl).then(embedUrl => {
      if (cancelled || !containerRef.current) return;

      // Create iframe element
      const iframe = document.createElement('iframe');
      iframe.setAttribute('src', embedUrl);
      iframe.setAttribute('allow', 'fullscreen');
      iframe.style.cssText = 'width:100%;height:100%;border:none;';

      iframeRef.current = iframe;

      const handleLoad = () => {
        setIsLoading(false);
        setStatus('Autodesk viewer loaded');
      };

      const handleError = () => {
        setIsLoading(false);
        setError('Failed to load viewer');
        setStatus('Failed to load Autodesk viewer');
      };

      iframe.addEventListener('load', handleLoad);
      iframe.addEventListener('error', handleError);

      containerRef.current!.appendChild(iframe);
    });

    return () => {
      cancelled = true;

      if (iframeRef.current && containerRef.current) {
        try {
          if (containerRef.current.contains(iframeRef.current)) {
            containerRef.current.removeChild(iframeRef.current);
          }
        } catch (e) {
          // Ignore
        }
        iframeRef.current = null;
      }
    };
  }, [selectedModel?.externalUrl, selectedModel?.type, selectedModel?.id, resolveEmbedUrl, setStatus]);

  // Initialize Three.js for IFC viewing
  useEffect(() => {
    if (!selectedModel?.ifcPath || selectedModel.type !== 'ifc' || !containerRef.current) return;

    const initIFCViewer = async () => {
      setIsLoading(true);
      setStatus('Loading IFC model...');

      try {
        const container = containerRef.current!;

        const data = await api.file.readBinary(selectedModel.ifcPath!);

        setStatus('Parsing IFC data...');

        const THREE = await import('three');
        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js');
        const { IFCLoader } = await import('web-ifc-three');

        const width = container.clientWidth;
        const height = container.clientHeight;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x1a1a1a);

        const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
        camera.position.set(15, 15, 15);

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        container.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(10, 20, 10);
        scene.add(directionalLight);

        const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
        directionalLight2.position.set(-10, 10, -10);
        scene.add(directionalLight2);

        const gridHelper = new THREE.GridHelper(50, 50, 0x333333, 0x262626);
        scene.add(gridHelper);

        const ifcLoader = new IFCLoader();
        await ifcLoader.ifcManager.setWasmPath('https://unpkg.com/web-ifc@0.0.44/');

        const ifcModel = await ifcLoader.parse(data);
        scene.add(ifcModel);

        const box = new THREE.Box3().setFromObject(ifcModel);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        ifcModel.position.sub(center);

        const maxDim = Math.max(size.x, size.y, size.z);
        camera.position.set(maxDim, maxDim, maxDim);
        controls.target.set(0, 0, 0);
        controls.update();

        let animationId: number;
        const animate = () => {
          animationId = requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        };
        animate();

        const handleResize = () => {
          const w = container.clientWidth;
          const h = container.clientHeight;
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h);
        };
        window.addEventListener('resize', handleResize);

        setStatus('IFC model loaded');
        setIsLoading(false);

        return () => {
          window.removeEventListener('resize', handleResize);
          cancelAnimationFrame(animationId);
          renderer.dispose();
          if (container.contains(renderer.domElement)) {
            container.removeChild(renderer.domElement);
          }
        };
      } catch (error) {
        console.error('Failed to load IFC model:', error);
        setStatus(`Failed to load IFC: ${(error as Error).message}`);
        setIsLoading(false);
      }
    };

    const cleanup = initIFCViewer();
    return () => {
      if (rendererRef.current) {
        const canvas = rendererRef.current.domElement;
        rendererRef.current.dispose();
        rendererRef.current.forceContextLoss();
        rendererRef.current = null;
        if (canvas && containerRef.current && containerRef.current.contains(canvas)) {
          containerRef.current.removeChild(canvas);
        }
      }
      cleanup?.then(fn => fn?.());
    };
  }, [selectedModel?.ifcPath, selectedModel?.type, selectedModel?.id, setStatus]);

  // Show empty state if no models
  if (models.length === 0) {
    return (
      <div className="viewer">
        <div className="viewer__setup">
          <div className="viewer__setup-content">
            <svg
              className="viewer__setup-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <path d="M3.27 6.96L12 12.01l8.73-5.05" />
              <path d="M12 22.08V12" />
            </svg>

            <h3 className="viewer__setup-title">No BIM Models</h3>

            <p className="viewer__setup-description">
              No BIM models available for this project
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render viewer with model list
  return (
    <div className="viewer" style={{ display: 'flex', flexDirection: 'row', position: 'relative', flex: 1, minHeight: 0 }}>
      {/* Model List Sidebar — collapsible, overlay on mobile */}
      <div className="viewer__model-list" style={{
        ...(isMobile && !sidebarCollapsed ? {
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: '220px',
          zIndex: 50,
          boxShadow: '4px 0 16px rgba(0,0,0,0.4)',
        } : {
          width: sidebarCollapsed ? '40px' : '220px',
          minWidth: sidebarCollapsed ? '40px' : '220px',
        }),
        borderRight: '1px solid var(--border-primary)',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-secondary)',
        transition: 'width 0.2s ease, min-width 0.2s ease',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: sidebarCollapsed ? '12px 8px' : '12px',
          borderBottom: '1px solid var(--border-primary)',
          display: 'flex',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          alignItems: 'center',
          gap: '4px',
        }}>
          <button
            className="viewer__toolbar-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand panel' : 'Collapse panel'}
            style={{ padding: '4px', flexShrink: 0 }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '16px', height: '16px', transform: sidebarCollapsed ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          {!sidebarCollapsed && (
            <span style={{ fontWeight: 500, fontSize: '13px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginRight: 10 }}>BIM Models ({models.length})</span>
          )}
        </div>

        {!sidebarCollapsed && (
          <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
            {models.map(model => (
              <div
                key={model.id}
                className={`viewer__model-item ${selectedModelId === model.id ? 'viewer__model-item--selected' : ''}`}
                onClick={() => { setSelectedModelId(model.id); if (isMobile) setSidebarCollapsed(true); }}
                style={{
                  padding: '10px 12px',
                  marginBottom: '4px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  background: selectedModelId === model.id ? 'var(--accent-primary)' : 'transparent',
                  color: selectedModelId === model.id ? '#000' : 'inherit',
                  transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => {
                  if (selectedModelId !== model.id) {
                    e.currentTarget.style.background = 'var(--bg-tertiary)';
                  }
                }}
                onMouseLeave={e => {
                  if (selectedModelId !== model.id) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
                    {model.type === 'external' ? (
                      <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    ) : (
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    )}
                  </svg>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: selectedModelId === model.id ? 500 : 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {model.name}
                  </span>
                </div>
                <div style={{
                  fontSize: '11px',
                  opacity: 0.7,
                  marginTop: '4px',
                  marginLeft: '24px',
                }}>
                  {model.type === 'external' ? 'Autodesk' : 'IFC File'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mobile backdrop to close sidebar */}
      {isMobile && !sidebarCollapsed && (
        <div
          onClick={() => setSidebarCollapsed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 40,
            background: 'rgba(0,0,0,0.3)',
          }}
        />
      )}

      {/* Main Viewer Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div className="viewer__content" style={{ flex: 1, position: 'relative' }}>
          <div
            ref={containerRef}
            className="viewer__webview-container"
            style={{ width: '100%', height: '100%' }}
          />

          {isLoading && (
            <div className="viewer__loading">
              <div className="viewer__loading-spinner" />
              <span className="viewer__loading-text">
                Loading {selectedModel?.type === 'external' ? 'Autodesk viewer' : 'IFC model'}...
              </span>
            </div>
          )}

          {error && (
            <div className="viewer__error">
              <p>{error}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
